import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Timer, Send, Loader2, Phone, Fingerprint } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUBJECTS = ["RFQ / RFP Response", "Teaming / Joint Venture", "Subcontracting Opportunity", "IT Services", "Equipment Sourcing", "General Inquiry"];

const inputCls =
  "w-full bg-[#0D0F12] border border-slate-700 text-slate-100 text-base sm:text-sm px-4 py-3 sm:py-2.5 min-h-[48px] sm:min-h-0 placeholder:text-slate-600 focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition-colors duration-200";

export default function Contact() {
  const [form, setForm] = useState({ name: "", organization: "", email: "", subject: SUBJECTS[0], message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Transmission received. The Defense Desk will respond within 24 hours.");
    } catch {
      toast.error("Transmission failed. Please email contracts@def-coin.com directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-16 md:py-24 lg:py-28 bg-[#14171A]/40 border-t border-slate-800" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 lg:gap-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-amber-600 mb-3 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-amber-600" /> 04 / Contact
          </p>
          <h2 className="font-display font-bold uppercase tracking-tight text-xl sm:text-2xl text-slate-50">
            Contact the Defense Desk
          </h2>
          <p className="mt-5 text-slate-400 text-sm leading-relaxed max-w-md">
            For RFQs, teaming inquiries, and subcontracting coordination across European operational theaters,
            transmit your requirement below or reach us directly.
          </p>

          <div className="mt-8 md:mt-10 space-y-5" data-testid="contact-info">
            <div className="flex items-start gap-4">
              <Mail className="w-4 h-4 text-blue-500 mt-1 shrink-0" />
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-1">Contracts</p>
                <a href="mailto:contracts@def-coin.com" className="text-slate-300 hover:text-amber-600 transition-colors text-sm" data-testid="contact-email-link">
                  contracts@def-coin.com
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Phone className="w-4 h-4 text-blue-500 mt-1 shrink-0" />
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-1">Phone</p>
                <a href="tel:+359876065815" className="text-slate-300 hover:text-amber-600 transition-colors text-sm" data-testid="contact-phone-link">
                  +359 87 606 5815
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <MapPin className="w-4 h-4 text-blue-500 mt-1 shrink-0" />
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-1">Location</p>
                <p className="text-slate-300 text-sm">DEFCOIN Ltd.</p>
                <p className="text-slate-400 text-sm leading-relaxed mt-1" data-testid="contact-location">Sofia, Bulgaria — EU / NATO Eastern Flank</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Fingerprint className="w-4 h-4 text-blue-500 mt-1 shrink-0" />
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-1">Registry Identifiers</p>
                <p className="font-mono text-xs text-slate-300 leading-relaxed" data-testid="contact-registry-ids">
                  UIC: 208073340 <span className="text-slate-600">|</span> VAT ID: BG208073340<br />
                  NATO NCAGE: <span className="text-amber-600">01GUU</span> <span className="text-slate-600">|</span> UEI: <span className="text-amber-600">FJNKCGJV1QQ6</span>
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Timer className="w-4 h-4 text-blue-500 mt-1 shrink-0" />
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-1">Response Time</p>
                <p className="text-slate-300 text-sm">RFQ turnaround within 24 hours, OCONUS tempo</p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          {sent ? (
            <div className="border border-amber-600/40 bg-[#0D0F12] p-6 sm:p-10 h-full flex flex-col justify-center" data-testid="contact-success">
              <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-amber-600 mb-4">// Transmission Confirmed</p>
              <h3 className="font-display font-bold uppercase tracking-tight text-lg text-slate-50 mb-3">Requirement received.</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Your enquiry has been logged with the DEFCOIN Defense Desk. Expect a response at the provided
                email within 24 hours.
              </p>
              <button
                onClick={() => { setSent(false); setForm({ name: "", organization: "", email: "", subject: SUBJECTS[0], message: "" }); }}
                className="mt-8 w-fit font-mono text-[11px] tracking-[0.15em] uppercase text-slate-400 hover:text-amber-600 border border-slate-700 hover:border-amber-600 px-5 py-2.5 transition-colors duration-200"
                data-testid="contact-send-another-btn"
              >
                Send Another Transmission
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-slate-800 bg-[#14171A] p-5 sm:p-7 lg:p-8 space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="cf-name" className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">Full Name *</label>
                  <input id="cf-name" required value={form.name} onChange={set("name")} placeholder="J. Smith" className={inputCls} data-testid="contact-name-input" />
                </div>
                <div>
                  <label htmlFor="cf-org" className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">Organization *</label>
                  <input id="cf-org" required value={form.organization} onChange={set("organization")} placeholder="Agency / Prime Contractor" className={inputCls} data-testid="contact-org-input" />
                </div>
              </div>
              <div>
                <label htmlFor="cf-email" className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">Official Email *</label>
                <input id="cf-email" type="email" required value={form.email} onChange={set("email")} placeholder="name@agency.mil" className={inputCls} data-testid="contact-email-input" />
              </div>
              <div>
                <label htmlFor="cf-subject" className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">Subject</label>
                <select id="cf-subject" value={form.subject} onChange={set("subject")} className={inputCls} data-testid="contact-subject-select">
                  {SUBJECTS.map((s) => (
                    <option key={s} value={s} className="bg-[#0D0F12]">{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="cf-message" className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">Requirement Details *</label>
                <textarea id="cf-message" required rows={5} value={form.message} onChange={set("message")} placeholder="Scope, theater, timeline, applicable NAICS..." className={`${inputCls} resize-none`} data-testid="contact-message-input" />
              </div>
              <button
                type="submit"
                disabled={sending}
                className="w-full flex items-center justify-center gap-3 min-h-[52px] bg-blue-600 hover:bg-[#55695A] disabled:opacity-60 text-white font-mono text-[11px] tracking-[0.15em] uppercase px-6 py-3.5 border border-blue-600 hover:border-amber-600 transition-colors duration-200"
                data-testid="contact-submit-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? "Transmitting..." : "Transmit Requirement"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
